import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { logQueueService } from '@/services/logQueue';
import { gpsService } from '@/services/gps';
import { mediaQueueService } from '@/services/mediaQueue';
import { assignmentRepo } from '@/data/repositories/assignmentRepo';
import { groupStops, isDockStop, type Stop } from '@/lib/grouping';
import { stopLogLocation } from '@/lib/logLocation';
import {
  DevSwitch,
  DispatchSwitch,
  LogTypes,
  buildLog,
  MediaFields,
  AvsDeliveryFields,
  CaptureFields,
} from '@/types/domain';
import type { OsdValue } from '@/types/domain';

export type DeliveryPhase =
  | 'idle'
  | 'enRoute'
  | 'terminalReturn'
  | 'atTerminal'
  | 'arrived'
  | 'unloading'
  | 'workBills'
  | 'signatureGate'
  | 'complete';

export interface DeliveryAvs {
  inside: boolean;
  tailgate: boolean;
  residential: boolean;
}

export const MAX_BILL_PHOTOS = 5;

interface BillState {
  noFreight: boolean;
  confirmed: boolean;
  notes: string;
  osd: OsdValue;
  photos: Blob[];
  podDoc: Blob | null;
}

interface DeliveryState {
  stop: Stop | null;
  isDock: boolean;
  phase: DeliveryPhase;
  signerName: string;
  avs: DeliveryAvs;
  bills: Record<string, BillState>;
  pendingSignature: Blob | null;
  error: string | null;

  loadStop: (idOrKey: string) => Promise<void>;
  startEnRoute: () => Promise<void>;
  arriveAtTerminal: () => Promise<void>;
  unloadAtDock: () => Promise<void>;
  arrive: () => Promise<void>;
  beginUnloading: () => Promise<void>;
  cancelEnRoute: () => Promise<void>;
  stepBack: () => Promise<void>;
  cancelUnloading: () => void;
  setSignerName: (name: string) => void;
  setAvs: (patch: Partial<DeliveryAvs>) => void;
  setBillNoFreight: (id: string, value: boolean) => void;
  setBillConfirmed: (id: string, value: boolean) => void;
  setBillNotes: (id: string, notes: string) => void;
  setBillOsd: (id: string, value: OsdValue) => void;
  addBillPhoto: (id: string, blob: Blob) => void;
  removeBillPhoto: (id: string, index: number) => void;
  setBillPodDoc: (id: string, blob: Blob | null) => void;
  captureSignature: (blob: Blob) => void;
  complete: () => Promise<void>;
  reset: () => void;
}

const emptyAvs: DeliveryAvs = { inside: false, tailgate: false, residential: false };
// Native writes uppercase YES/NO to FileMaker.
const yn = (b: boolean) => (b ? 'YES' : 'NO');

async function findStop(idOrKey: string): Promise<Stop | undefined> {
  const items = await assignmentRepo.getUnloadItems();
  const stops = [...groupStops(items, 'delivery', false), ...groupStops(items, 'delivery', true)];
  return stops.find((s) => s.key === idOrKey || s.bills.some((b) => b.remF04Id === idOrKey));
}

const freshBills = (stop: Stop): Record<string, BillState> =>
  Object.fromEntries(
    stop.bills.map((b) => [
      b.remF04Id,
      { noFreight: false, confirmed: false, notes: '', osd: 'Clear' as OsdValue, photos: [], podDoc: null },
    ]),
  );

export const useDeliveryStore = create<DeliveryState>()(
  persist(
    (set, get) => ({
  stop: null,
  isDock: false,
  phase: 'idle',
  signerName: '',
  avs: { ...emptyAvs },
  bills: {},
  pendingSignature: null,
  error: null,

  loadStop: async (idOrKey) => {
    const stop = await findStop(idOrKey);
    if (!stop) {
      set({ error: 'Stop not found', stop: null });
      return;
    }
    const isDock = stop.bills.some((b) => isDockStop('delivery', b));
    set({
      stop,
      isDock,
      phase: 'idle',
      signerName: '',
      avs: { ...emptyAvs },
      bills: freshBills(stop),
      pendingSignature: null,
      error: null,
    });
  },

  startEnRoute: async () => {
    const { stop, isDock } = get();
    if (!stop) return;
    // Native keeps the switch at 2 on delivery en-route; unloading moves it to 5.
    const loc = stopLogLocation(stop);
    const fix = gpsService.peekFix();
    if (isDock) {
      // Dock delivery: head back to the terminal instead of a consignee.
      set({ phase: 'terminalReturn' });
      for (const b of stop.bills) {
        await logQueueService.enqueue({
          logType: LogTypes.backToBase,
          display: 'Display',
          assignmentId: b.remF04Id,
          tag: b.remF02Tag ?? undefined,
          latitude: fix?.latitude,
          longitude: fix?.longitude,
          accuracy: fix?.accuracy,
        });
      }
    } else {
      set({ phase: 'enRoute' });
      for (const b of stop.bills) {
        await logQueueService.enqueue({
          logType: buildLog.enRouteToDelivery(loc),
          display: 'Display',
          assignmentId: b.remF04Id,
          tag: b.remF02Tag ?? undefined,
          latitude: fix?.latitude,
          longitude: fix?.longitude,
          accuracy: fix?.accuracy,
        });
      }
    }
    // Delivery en-route bills are switch 2, so route tracking emits `gps break`
    // for them every 5 min (matches native).
    gpsService.startRouteTracking();
  },

  arriveAtTerminal: async () => {
    const stop = get().stop;
    if (!stop) return;
    for (const b of stop.bills) {
      await gpsService.captureOnce({
        assignmentId: b.remF04Id,
        display: 'Display',
        logType: LogTypes.arrivedAtTerminal,
        tag: b.remF02Tag ?? undefined,
      });
    }
    set({ phase: 'atTerminal' });
  },

  unloadAtDock: async () => {
    const stop = get().stop;
    if (!stop) return;
    // Native dock delivery is a batch drop -- no bill work / POD signature.
    // Log the dock unload and complete every bill.
    for (const b of stop.bills) {
      await logQueueService.enqueue({
        logType: LogTypes.unloadedAtTerminalDock,
        display: 'Display',
        assignmentId: b.remF04Id,
        tag: b.remF02Tag ?? undefined,
      });
      await assignmentRepo.markComplete(b.remF04Id);
    }
    set({ phase: 'complete' });
  },

  arrive: async () => {
    const stop = get().stop;
    if (!stop) return;
    const loc = stopLogLocation(stop);
    for (const b of stop.bills) {
      await gpsService.captureOnce({
        assignmentId: b.remF04Id,
        display: 'Display',
        logType: buildLog.arrivedAtDelivery(loc),
        tag: b.remF02Tag ?? undefined,
      });
    }
    set({ phase: 'arrived' });
  },

  beginUnloading: async () => {
    const stop = get().stop;
    if (!stop) return;
    const loc = stopLogLocation(stop);
    for (const b of stop.bills) {
      await logQueueService.enqueue({
        logType: buildLog.unloadingAt(loc),
        display: 'Display',
        assignmentId: b.remF04Id,
        tag: b.remF02Tag ?? undefined,
      });
      // Native moves the bill to active-delivery (switch 5) on unloading start.
      await assignmentRepo.setLocalSwitch(b.remF04Id, DevSwitch.Delivery);
    }
    // Native goes straight to bill work after the UNLOADING AT log.
    set({ phase: 'workBills' });
  },

  cancelEnRoute: async () => {
    const stop = get().stop;
    if (!stop) return;
    await gpsService.stopRouteTracking();
    const loc = stopLogLocation(stop);
    const fix = gpsService.peekFix();
    for (const b of stop.bills) {
      await logQueueService.enqueue({
        logType: buildLog.enRouteDeliveryCancelled(loc),
        display: 'Display',
        assignmentId: b.remF04Id,
        tag: b.remF02Tag ?? undefined,
        latitude: fix?.latitude,
        longitude: fix?.longitude,
        accuracy: fix?.accuracy,
      });
    }
    await logQueueService.flush();
    set({ phase: 'idle' });
  },

  cancelUnloading: () => {
    const stop = get().stop;
    if (!stop) return;
    set({ phase: 'arrived', bills: freshBills(stop) });
  },

  stepBack: async () => {
    const phase = get().phase;
    switch (phase) {
      case 'enRoute':
        await get().cancelEnRoute();
        break;
      case 'workBills':
        get().cancelUnloading();
        break;
      case 'signatureGate':
        set({ phase: 'workBills', pendingSignature: null });
        break;
      default:
        break;
    }
  },

  setSignerName: (name) => set({ signerName: name }),
  setAvs: (patch) => set((s) => ({ avs: { ...s.avs, ...patch } })),
  setBillNoFreight: (id, value) =>
    set((s) => ({ bills: { ...s.bills, [id]: { ...s.bills[id], noFreight: value } } })),
  setBillConfirmed: (id, value) =>
    set((s) => ({ bills: { ...s.bills, [id]: { ...s.bills[id], confirmed: value } } })),
  setBillNotes: (id, notes) =>
    set((s) => ({ bills: { ...s.bills, [id]: { ...s.bills[id], notes } } })),
  setBillOsd: (id, value) =>
    set((s) => ({ bills: { ...s.bills, [id]: { ...s.bills[id], osd: value } } })),
  addBillPhoto: (id, blob) =>
    set((s) => {
      const current = s.bills[id]?.photos ?? [];
      if (current.length >= MAX_BILL_PHOTOS) return s;
      return { bills: { ...s.bills, [id]: { ...s.bills[id], photos: [...current, blob] } } };
    }),
  removeBillPhoto: (id, index) =>
    set((s) => ({
      bills: {
        ...s.bills,
        [id]: { ...s.bills[id], photos: (s.bills[id]?.photos ?? []).filter((_, i) => i !== index) },
      },
    })),
  setBillPodDoc: (id, blob) =>
    set((s) => ({ bills: { ...s.bills, [id]: { ...s.bills[id], podDoc: blob } } })),
  captureSignature: (blob) => set({ pendingSignature: blob }),

  complete: async () => {
    const { stop, signerName, avs, bills, pendingSignature } = get();
    if (!stop) return;
    const loc = stopLogLocation(stop);
    const fix = gpsService.peekFix();

    for (const b of stop.bills) {
      const bs = bills[b.remF04Id] ?? {
        noFreight: false,
        confirmed: false,
        notes: '',
        osd: 'Clear' as OsdValue,
        photos: [],
        podDoc: null,
      };
      if (bs.noFreight) {
        await logQueueService.enqueue({
          logType: buildLog.noFreightDelivery(loc),
          display: 'Display',
          assignmentId: b.remF04Id,
          tag: b.remF02Tag ?? undefined,
          additionalFields: { [CaptureFields.switch999]: '999' },
        });
        // Native repositions undelivered freight: dispatch type -> '2' (to dock).
        await assignmentRepo.updateDispatchType(b.remF04Id, DispatchSwitch.ShipperToDock);
        await assignmentRepo.markComplete(b.remF04Id);
        continue;
      }

      await logQueueService.enqueue({
        logType: buildLog.finishUnloadingAt(loc),
        display: 'Display',
        assignmentId: b.remF04Id,
        tag: b.remF02Tag ?? undefined,
      });
      const departed = await logQueueService.enqueue({
        logType: buildLog.departedFrom(loc),
        display: 'Display',
        assignmentId: b.remF04Id,
        tag: b.remF02Tag ?? undefined,
        latitude: fix?.latitude,
        longitude: fix?.longitude,
        accuracy: fix?.accuracy,
        additionalFields: {
          [CaptureFields.deliveryName]: signerName,
          [CaptureFields.deliveryNotes]: bs.notes || null,
          [CaptureFields.deliveryOsd]: bs.osd,
          [AvsDeliveryFields.inside]: yn(avs.inside),
          [AvsDeliveryFields.tailgate]: yn(avs.tailgate),
          [AvsDeliveryFields.residential]: yn(avs.residential),
        },
      });
      if (pendingSignature) {
        await mediaQueueService.enqueue({
          kind: 'delivery-signature',
          assignmentId: b.remF04Id,
          logClientId: departed.id,
          fieldName: MediaFields.deliverySignature,
          blob: pendingSignature,
          filename: `delivery-sig-${b.remF04Id}-${Date.now()}.png`,
          mimeType: 'image/png',
          tag: b.remF02Tag,
          driverNumber: b.remF01User,
        });
      }
      for (let i = 0; i < bs.photos.length; i++) {
        const blob = bs.photos[i];
        const slot = (i + 1) as 1 | 2 | 3 | 4 | 5;
        await mediaQueueService.enqueue({
          kind: 'delivery-photo',
          assignmentId: b.remF04Id,
          logClientId: departed.id,
          fieldName: MediaFields.deliveryPhoto(slot),
          blob,
          filename: `delivery-${b.remF04Id}-${slot}-${Date.now()}.jpg`,
          mimeType: blob.type || 'image/jpeg',
          tag: b.remF02Tag,
          driverNumber: b.remF01User,
        });
      }
      if (bs.podDoc) {
        await mediaQueueService.enqueue({
          kind: 'pod-doc',
          assignmentId: b.remF04Id,
          logClientId: departed.id,
          fieldName: MediaFields.podMaster,
          blob: bs.podDoc,
          filename: `pod-${b.remF04Id}-${Date.now()}.jpg`,
          mimeType: bs.podDoc.type || 'image/jpeg',
          tag: b.remF02Tag,
          driverNumber: b.remF01User,
        });
      }
      await assignmentRepo.markComplete(b.remF04Id);
    }

    set({ phase: 'complete' });
  },

  reset: () =>
    set({
      stop: null,
      isDock: false,
      phase: 'idle',
      signerName: '',
      avs: { ...emptyAvs },
      bills: {},
      pendingSignature: null,
      error: null,
    }),
    }),
    {
      name: 'delivery-workflow',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (s) => ({
        stop: s.stop,
        isDock: s.isDock,
        phase: s.phase,
        signerName: s.signerName,
        avs: s.avs,
        bills: Object.fromEntries(
          Object.entries(s.bills).map(([k, b]) => [
            k,
            {
              noFreight: b.noFreight,
              confirmed: b.confirmed,
              notes: b.notes,
              osd: b.osd,
              photos: [],
              podDoc: null,
            },
          ]),
        ),
      }),
    },
  ),
);

export const deliverySelectors = {
  // Native gate: every bill handled (confirmed or no-freight) AND at least one
  // real delivery confirmed.
  canContinueToSignature: (s: DeliveryState) => {
    if (!s.stop) return false;
    const ids = s.stop.bills.map((b) => b.remF04Id);
    const allHandled = ids.every((id) => s.bills[id]?.confirmed || s.bills[id]?.noFreight);
    const someConfirmed = ids.some((id) => s.bills[id]?.confirmed);
    return allHandled && someConfirmed;
  },

  // Receiver signature is mandatory only when a delivered bill is missing its
  // scanned POD document. Smaller carriers deliver on the interline carrier's
  // DR: when every confirmed bill has that document scanned, the scan is the
  // legal POD and the signature becomes optional (still offered).
  signatureRequired: (s: DeliveryState) => {
    if (!s.stop) return true;
    const delivered = s.stop.bills.filter(
      (b) => s.bills[b.remF04Id]?.confirmed && !s.bills[b.remF04Id]?.noFreight,
    );
    if (delivered.length === 0) return true;
    return delivered.some((b) => !s.bills[b.remF04Id]?.podDoc);
  },
};
